import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AlarmIcon from '../../assets/icons/alarm.svg?react';
import DownloadIcon from '../../assets/icons/download.svg?react';
import GraphIcon from '../../assets/icons/graph.svg?react';
import MachineIcon from '../../assets/icons/machine.svg?react';
import TableFilterIcon from '../../assets/icons/table-filter.svg?react';
import { ResponsiveDataView } from '../../components/data-display/ResponsiveDataView';
import { AddAlarmDialog } from '../../components/dialogs/AddAlarmDialog';
import { ActiveFilterTags } from '../../components/filters/ActiveFilterTags';
import { MobileFilters } from '../../components/filters/MobileFilters';
import { useMachineFilterTags } from '../../components/filters/useMachineFilterTags';
import { FiltersMobile } from '../../components/FiltersMobile';
import { IconButton } from '../../components/IconButton';
import { MachineSidePanel } from '../../components/machines';
import { Toast, ToastProvider } from '../../components/Toast';
import { useHeader } from '../../contexts/HeaderContext';
import { useMobileFilter } from '../../contexts/MobileFilterContext';
import {
  Machine as GeneratedMachine,
  MachinesOrderByInput,
  SortOrder,
} from '../../gql/generated';
import { useMachineFilters } from '../../hooks/useMachineFilters';
import { Machine } from '../../types/machines/machine.types';
import { openMachineGraphs } from '../../utils/graphNavigation';
import { createMachineColumns } from './createMachineColumns';
import { getDefaultColumnVisibility } from './machineColumnConfig';
import { MachineListItemMobile } from './MachineListItemMobile';
import { TableMultiSelectActions } from './TableMultiSelectActions';
import { TableMultiSelectAction } from './types';
import { useMachineColumnFilters } from './useMachineColumnFilters';
import { useMachinesExport } from './useMachinesExport';
import { useMachinesTable } from './useMachinesTable';
import { logger } from '../../utils/logger';

export function MachinesPage() {
  const navigate = useNavigate();
  const state = useMachinesTable();
  const { hasActiveFilters, clearFilters } = useMachineFilters();
  const filterTags = useMachineFilterTags();
  const { setMobileActionButton } = useHeader();
  const { toggle, close } = useMobileFilter();
  const [alarmDialogOpen, setAlarmDialogOpen] = useState(false);
  const [selectedMachineForPanel, setSelectedMachineForPanel] =
    useState<GeneratedMachine | null>(null);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<'success' | 'error'>(
    'success'
  );
  const { exportMachines, exporting } = useMachinesExport();

  // Get the column filter render function (reads filters directly from URL)
  const renderColumnFilter = useMachineColumnFilters();

  // Set up the filter button in the header for mobile
  useLayoutEffect(() => {
    setMobileActionButton(
      <div className='relative'>
        <IconButton
          aria-label='Toggle filters'
          icon={<TableFilterIcon width={24} height={24} />}
          onClick={toggle}
          className='hover:bg-transparent [&_path]:stroke-white'
        />
        {hasActiveFilters && (
          <div
            className='bg-focus border-surface absolute top-0 right-0 h-2.5
              w-2.5 rounded-full border-2'
          />
        )}
      </div>
    );

    return () => {
      setMobileActionButton(null);
    };
  }, [setMobileActionButton, toggle, hasActiveFilters]);

  const columns = useMemo(
    () =>
      createMachineColumns({
        selectedMachineIds: state.selectedMachineIds,
        onSelectionChange: state.setSelectedMachineIds,
        onAddAlarm: (machineId) => {
          // Set selection to just this machine and open dialog
          state.setSelectedMachineIds([machineId]);
          setAlarmDialogOpen(true);
        },
      }),
    [state]
  );

  const handleClearFilters = () => {
    clearFilters();
    close();
  };

  // Helper to convert selected database IDs to machineIds
  const getSelectedMachineIds = (): string[] => {
    return state.machines
      .filter((machine) => state.selectedMachineIds.includes(machine.id))
      .map((machine) => machine.machineId);
  };

  // Keep the export sorted the same way the table is
  const exportOrderBy = useMemo<MachinesOrderByInput | undefined>(() => {
    const [firstSort] = state.sorting;
    if (!firstSort) return undefined;

    return {
      [firstSort.id]: firstSort.desc ? SortOrder.desc : SortOrder.asc,
    } as MachinesOrderByInput;
  }, [state.sorting]);

  const handleExport = useCallback(
    async (machineIds?: string[]) => {
      try {
        const count = await exportMachines({
          machineIds,
          orderBy: exportOrderBy,
        });
        setToastMessage(
          count > 0
            ? `✓ Exported ${count} machine${count === 1 ? '' : 's'} to CSV`
            : 'No machines matched the current filters'
        );
        setToastVariant(count > 0 ? 'success' : 'error');
      } catch (error) {
        logger.error('Failed to export machines:', error);
        setToastMessage('Could not export machines. Please try again.');
        setToastVariant('error');
      }
      setToastOpen(true);
    },
    [exportMachines, exportOrderBy]
  );

  // CSV export is desktop-only, so it is not part of the shared actions
  const sharedSelectActions: TableMultiSelectAction[] = [
    {
      label: 'Show graphs',
      onClick: () => openMachineGraphs(getSelectedMachineIds()),
      icon: <GraphIcon />,
    },
    {
      label: 'Add Pulse alert',
      onClick: () => setAlarmDialogOpen(true),
      icon: <AlarmIcon />,
      variant: 'primary',
    },
  ];

  const multiSelectActions: TableMultiSelectAction[] = [
    {
      label: exporting ? 'Exporting...' : 'Export selection (CSV)',
      onClick: () => handleExport(state.selectedMachineIds),
      icon: <DownloadIcon />,
    },
    ...sharedSelectActions,
  ];

  // Mobile-specific selection actions
  const mobileSelectionActions = sharedSelectActions.map((action) => ({
    label: action.label,
    onClick: action.onClick,
    icon: action.icon,
    variant: action.variant,
  }));

  // Handle row click - open side panel with machine details
  const handleRowClick = (machine: Machine) => {
    // Use the _original GraphQL machine data for the side panel
    if (machine._original) {
      setSelectedMachineForPanel(machine._original);
      setSidePanelOpen(true);
    }
  };

  // Handle side panel close - close first, then clear machine after animation
  const handleSidePanelClose = (open: boolean) => {
    if (!open) {
      setSidePanelOpen(false);
      // Clear machine after animation completes (300ms)
      setTimeout(() => setSelectedMachineForPanel(null), 350);
    }
  };

  const handleAlarmCreated = useCallback(
    (alarmId: string) => {
      // Navigate to alarms page with highlight parameter
      navigate(`/alarms?highlight=${alarmId}`);
    },
    [navigate]
  );

  return (
    <ToastProvider>
      {/* Machine Side Panel */}
      <MachineSidePanel
        open={sidePanelOpen}
        onOpenChange={handleSidePanelClose}
        machine={selectedMachineForPanel}
      />

      <Toast
        open={toastOpen}
        onOpenChange={setToastOpen}
        description={toastMessage}
        variant={toastVariant}
      />

      {/* Alarm Dialog */}
      <AddAlarmDialog
        open={alarmDialogOpen}
        onOpenChange={setAlarmDialogOpen}
        selectedMachineIds={state.selectedMachineIds}
        availableMachines={state.machines.map((m) => ({
          id: m.id,
          displayName: m.displayName || m.machineId,
        }))}
        onAlarmCreated={handleAlarmCreated}
      />

      {/* Mobile Filter Panel */}
      <FiltersMobile
        onClear={handleClearFilters}
        hasActiveFilters={hasActiveFilters}
      >
        <MobileFilters />
      </FiltersMobile>

      {/* Main Content */}
      <ResponsiveDataView
        data={state.machines}
        isLoading={state.loading}
        onRefresh={async () => {
          await state.refetch();
        }}
        error={state.error}
        getRowId={(machine) => machine.id}
        columns={columns}
        pageIndex={state.pageIndex}
        pageSize={state.pageSize}
        onPageChange={state.setPageIndex}
        onPageSizeChange={state.setPageSize}
        sorting={state.sorting}
        onSortingChange={state.setSorting}
        columnPinning={state.columnPinning}
        onColumnPinningChange={state.setColumnPinning}
        columnVisibility={state.columnVisibility}
        onColumnVisibilityChange={state.setColumnVisibility}
        defaultColumnVisibility={getDefaultColumnVisibility()}
        columnOrder={state.columnOrder}
        onColumnOrderChange={state.setColumnOrder}
        columnSizing={state.columnSizing}
        onColumnSizingChange={state.onColumnSizingChange}
        resetColumnSizing={state.resetColumnSizing}
        renderListItem={(machine, config) => (
          <MachineListItemMobile machine={machine} config={config} />
        )}
        enableSelection
        selectedIds={state.selectedMachineIds}
        onSelectionChange={state.setSelectedMachineIds}
        onRowClick={handleRowClick}
        totalPages={state.totalPages}
        totalCount={state.totalCount}
        columnFilters={renderColumnFilter}
        activeFilters={
          hasActiveFilters ? (
            <ActiveFilterTags tags={filterTags} onClearAll={clearFilters} />
          ) : undefined
        }
        multiSelectActions={
          <TableMultiSelectActions
            selectedCount={state.selectedMachineIds.length}
            actions={multiSelectActions}
            onClearSelection={() => state.setSelectedMachineIds([])}
          />
        }
        mobileSelectionActions={mobileSelectionActions}
        onClearSelection={() => state.setSelectedMachineIds([])}
        enableManageColumns
        emptyStateMessage='No machines found. Try adjusting your filters.'
        ariaLabel='Machines'
        title='Machines'
        icon={<MachineIcon />}
      />
    </ToastProvider>
  );
}
